import { useEffect } from 'react';
import { Button, Typography, Box, Stack, Card, CardContent } from '@mui/material'
import { ReactComponent as Mail } from '@assets/images/icon/ico_mail.svg'
import { ReactComponent as Phone } from '@assets/images/icon/ico_phone.svg'
import { useIsMobile } from '@/hooks/Responsive'
import { useNavigate, useLocation } from 'react-router-dom'
import PhoneIdentityVerification from "@components/PhoneIdentityVerification";
import { phoneCertificationCheck } from "@api/login";
import { useConfirm } from '@components/ConfirmProvider'
import { useLang } from '@/i18n/useLang'
import { buildLangHref } from '@data/nav'

export default function InactivityPhone() {
  const { isMobile } = useIsMobile()
  const lang = useLang() 
  const { alert, confirm } = useConfirm();
  const navigate = useNavigate()
  const location = useLocation();
  const { publicId, fromPortone } = location.state || {};

  // 1) 최초 진입 시 accountId 정보 localStorage에 저장 (본인인증 모바일 콜백에서 재사용)
  useEffect(() => {
    if (publicId != null) {
      localStorage.setItem("InactivityPhone.publicId", publicId);
    }
  }, [publicId]);

  // 2) PortOne 콜백에서 돌아온 경우 : 저장된 verified 로 기존 doPortOne 재호출
  useEffect(() => {
    if (fromPortone) {
      const stored = localStorage.getItem("PortOne.Inactivity.verified");
      const publicIdCallBack = localStorage.getItem("InactivityPhone.publicId");

      if (stored) {
        try {
          const data = JSON.parse(stored);
          doPortOne(data, publicIdCallBack);       // 기존 로직 재사용
        }
        catch (e) {
          console.error(e);
        }
        finally {
          localStorage.removeItem("InactivityPhone.publicId");
          localStorage.removeItem("PortOne.Inactivity.verified");
        }
      }

      // fromPortone 플래그 제거 (뒤로가기/새로고침 시 중복 처리 방지)
      navigate('.', {
        replace: true,
        state: { publicId },
      });
    }
  }, [fromPortone]);   // 한 번만 실행되도록

  // PortOne인증 콜백 함수
  const doPortOne = async (data, publicIdCallBack) => {
    const currentPublicId = publicId ?? publicIdCallBack;

    if (data?.status == 'VERIFIED') {
      try {
        const params = {
          publicId: currentPublicId,
          memberName: data?.name,
          birthdate: data?.birthDate.slice(0, 4),
          cellphoneNo: data?.phone,
          certToken: data?.certToken,
        };
        const response = await phoneCertificationCheck(params);

        if (response?.success) {
          navigate(buildLangHref('/inactivity/later', lang));
        }
        else {
          alert(response?.message || "오류가 발생했습니다.");
        }
      } catch (error) {
        console.error(error);
      }
    }
    else {
      alert("본인인증을 실패했습니다.");
    }
  };

  return (
    <Box>
      <Typography component="h3" variant={isMobile ? 'h6' : 'h5'} sx={{ mb: 1.5 }}>
        휴대전화 본인 인증
      </Typography>
      <Typography component="p" variant={isMobile ? 'body1' : 'subtitle2'} fontWeight={400} sx={{ mb: isMobile ? 4 : 6 }}>
        OpenVLab은 개인정보에 대한 유출 및 도용을 방지하기 위해 본인인증을 시행하고 있습니다.<br />
        <Typography component="span" variant={isMobile ? 'body1' : 'subtitle2'} fontWeight={500} color="primary.main">「정보통신망 이용촉진 및 정보보호 등에 관한 법률 제23조 2항(주민번호 사용 제한)」에 의거</Typography>하여, 웹사이트 내 주민등록번호 수집·이용이 금지되어 주민번호 입력 기반의 인증 서비스 사용이 제한됩니다.
      </Typography>

      <Stack
        direction={isMobile ? 'column' : 'row'}
        spacing={3}
        sx={{ mb: 1.5 }}
      >

        {/* 휴대전화 인증 */}
        <Card
          variant="outlined"
          sx={{
            flex: 1,
            borderRadius: 3,
          }}
        >
          <CardContent
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 3,
              minHeight: '136px',
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
            >
              <Box component={Phone} />
              <Typography variant="subtitle2" fontWeight={500}>
                휴대전화 번호로 본인 인증
              </Typography>
            </Stack>

            <PhoneIdentityVerification
              onSuccess={doPortOne}
              mode="inactivity"
            />

          </CardContent>
        </Card>
      </Stack>
    </Box>
  )
}